import {ReactiveController} from 'lit';
import type {O2dLayoutElement} from "../elements/o2d-layout.element";
import type {O2dAreaElement} from "../elements/o2d-area.element";
import {ScrollState} from "../types/scroll-state.interface";
import {ScrollConfig} from "../types/scroll-config.interface";
import {ScrollEventDetail} from "../types/scroll-event-detail.interface";
import {findTargetItemId} from "../utils/find-target-item-id";

/** Movement (px) after which a touch counts as a swipe rather than a tap. */
export const TOUCH_PAN_THRESHOLD_PX = 6;

/**
 * ScrollController handles touch, pointer and wheel scrolling for the grid.
 * Implements threshold-based scrolling with remainder tracking.
 *
 * Right-click behavior:
 * - Right-click + drag: Scrolls the grid (default behavior)
 * - Right-click without drag (< 3px movement): Dispatches 'cell-clicked' event on the area element
 *   via handleRightClickWithoutDrag() method
 * - Context menu is always prevented
 */
export class ScrollController implements ReactiveController {

    private readonly host: O2dLayoutElement;

    /**
     * Current scroll state
     */
    private scrollStatesByAxisId = new Map<string, ScrollState>();

    /**
     * Configuration
     */
    private config: ScrollConfig = {
        threshold: {x: 20, y: 35},
        enableTouch: true,
        enablePointer: true,
        enableWheel: true,
        touchSensitivity: 1,
        wheelSensitivity: 1
    };

    /**
     * Drag session tracking
     */
    private dragSession: {
        active: boolean
        startCoords: { x: number; y: number }
        lastCoords: { x: number; y: number }
        target: O2dAreaElement
    } | null = null;

    /**
     * Touch session tracking
     */
    private touchSession: {
        active: boolean
        /** True once the finger moved past {@link TOUCH_PAN_THRESHOLD_PX}; before that it is a tap. */
        panning: boolean
        startCoords: { x: number; y: number }
        lastCoords: { x: number; y: number }
        target: O2dAreaElement
    } | null = null;

    /**
     * Bound event handlers for cleanup
     */
    private boundHandlers: {
        pointerMove?: (e: PointerEvent) => void;
        pointerUp?: (e: PointerEvent) => void;
        pointerCancel?: (e: PointerEvent) => void;
        keyDown?: (e: KeyboardEvent) => void;
    } = {};

    constructor(host: O2dLayoutElement) {
        this.host = host;
        host.addController(this);
    }

    /**
     * Lifecycle: Called when host is connected to DOM
     */
    hostConnected(): void {
        this.updateThresholdFromCSS();
        this.attachEventListeners();
    }

    /**
     * Lifecycle: Called when host is disconnected from DOM
     */
    hostDisconnected(): void {
        // Clean up document-level event listeners
        this.removeDocumentListeners();
    }

    /**
     * Remove document-level event listeners
     */
    private removeDocumentListeners(): void {
        if (this.boundHandlers.pointerMove) {
            document.removeEventListener('pointermove', this.boundHandlers.pointerMove);
        }
        if (this.boundHandlers.pointerUp) {
            document.removeEventListener('pointerup', this.boundHandlers.pointerUp);
        }
        if (this.boundHandlers.pointerCancel) {
            document.removeEventListener('pointercancel', this.boundHandlers.pointerCancel);
        }
        if (this.boundHandlers.keyDown) {
            document.removeEventListener('keydown', this.boundHandlers.keyDown);
        }
        this.boundHandlers = {};
    }

    /**
     * Read threshold values from CSS variables
     */
    private updateThresholdFromCSS(): void {
        const styles = getComputedStyle(this.host);

        // Fallback to template-based variables if timeline ones not found
        const template = (this.host as any).template || 't1';

        const contentWidth = styles.getPropertyValue(`--${template}_cols_content__width`);
        if (contentWidth) {
            const parsed = parseFloat(contentWidth);
            if (!isNaN(parsed) && parsed > 0) {
                this.config.threshold.x = parsed;
            }
        }

        const templateRowHeight = styles.getPropertyValue(`--${template}_rows_content__height`);
        if (templateRowHeight) {
            const parsed = parseFloat(templateRowHeight);
            if (!isNaN(parsed) && parsed > 0) {
                this.config.threshold.y = parsed;
            }
        }
    }

    /**
     * Attach all event listeners
     */
    private attachEventListeners(): void {
        if (this.config.enableTouch) {
            this.attachTouchListeners();
        }
        if (this.config.enablePointer) {
            this.attachPointerListeners();
        }
        if (this.config.enableWheel) {
            this.attachWheelListeners();
        }
    }

    /**
     * Dispatch scroll event
     */
    private dispatchScrollEvent(
        deltaX: number,
        deltaY: number,
        areaElement: O2dAreaElement,
        mode: 'touch' | 'pointer' | 'wheel'
    ): void {
        if (deltaX === 0 && deltaY === 0) {
            return;
        }

         this.host.fireEvent<ScrollEventDetail>('layout-scrolled', {
            deltaX,
            deltaY,
            areaElement,
            mode
        });
    }

    /**
     * Attach touch event listeners
     */
    private attachTouchListeners(): void {
        this.host.addEventListener('touchstart', (e: TouchEvent) => {
            if (e.touches.length === 1 && e.touches[0]) {
                const touch = e.touches[0];
                const target = this.findAreaElement(e);
                if (!target) return;
                this.touchSession = {
                    active: true,
                    panning: false,
                    startCoords: {
                        x: touch.clientX,
                        y: touch.clientY
                    },
                    lastCoords: {
                        x: touch.clientX,
                        y: touch.clientY
                    },
                    target
                };
                // No preventDefault() here (Anke, 2026-10-17: "viele tasten (event klick) im
                // touchumgebung nicht funktionieren"): cancelling touchstart also cancels the click
                // the browser synthesises for a tap, so nothing inside the timeline — cards, buttons,
                // names — could be tapped. The touchmove handler below still cancels the native
                // pan once the finger actually moves, so scrolling stays ours.
            }
        }, {passive: false});

        this.host.addEventListener('touchmove', (e: TouchEvent) => {
            if (this.touchSession && this.touchSession.active && e.touches.length === 1 && e.touches[0]) {
                const touch = e.touches[0];
                const currentX = touch.clientX;
                const currentY = touch.clientY;

                // A finger jitters a pixel or two during a tap. Below the threshold the touch stays
                // a tap: no scroll, no preventDefault, so every browser still fires its click.
                if (!this.touchSession.panning) {
                    const distance = Math.hypot(currentX - this.touchSession.startCoords.x, currentY - this.touchSession.startCoords.y);
                    if (distance < TOUCH_PAN_THRESHOLD_PX) {
                        return;
                    }
                    this.touchSession.panning = true;
                }

                // Calculate delta from last position
                const deltaX = currentX - this.touchSession.lastCoords.x;
                const deltaY = currentY - this.touchSession.lastCoords.y;

                // Apply sensitivity, NOT inverted: the picture follows the finger, exactly like a
                // right-click drag (see pointerMove). Only the wheel inverts. Inverting here made
                // touch scroll against the finger on both axes (Anke, 2026-10-16).
                const adjustedDeltaX = deltaX * (this.config.touchSensitivity || 1);
                const adjustedDeltaY = deltaY * (this.config.touchSensitivity || 1);

                const areaElement = this.touchSession.target;

                // Process the scroll
                this.dispatchScrollEvent(adjustedDeltaX, adjustedDeltaY, areaElement, 'touch');

                // Update last position
                this.touchSession.lastCoords = {x: currentX, y: currentY};

                e.preventDefault();
            }
        }, {passive: false});

        this.host.addEventListener('touchend', () => {
            this.touchSession = null;
        });

        this.host.addEventListener('touchcancel', () => {
            this.touchSession = null;
        });
    }

    /**
     * Attach pointer event listeners (right-click drag)
     */
    private attachPointerListeners(): void {
        // Prevent context menu on right-click
        // Note: Always prevent contextmenu to avoid browser default menu
        this.host.addEventListener('contextmenu', (e: MouseEvent) => {
            e.preventDefault();
        });

        // Store current pointerId for tracking
        let currentPointerId: number | null = null;

        this.host.addEventListener('pointerdown', (e: PointerEvent) => {
            if (e.button === 2) { // Right-click
                const target = this.findAreaElement(e);
                if (!target) return;
                this.dragSession = {
                    active: true,
                    startCoords: {x: e.clientX, y: e.clientY},
                    lastCoords: {x: e.clientX, y: e.clientY},
                    target
                };
                currentPointerId = e.pointerId;

                // Add document-level listeners when drag starts
                this.addDocumentListeners(currentPointerId);

                e.preventDefault();
            }
        });
    }

    /**
     * Add document-level event listeners for pointer drag
     */
    private addDocumentListeners(pointerId: number): void {
        // Remove any existing listeners first
        this.removeDocumentListeners();

        // Create bound handlers
        this.boundHandlers.pointerMove = (e: PointerEvent) => {
            if (this.dragSession && this.dragSession.active && e.pointerId === pointerId) {
                // Calculate delta from the last position
                const deltaX = e.clientX - this.dragSession.lastCoords.x;
                const deltaY = e.clientY - this.dragSession.lastCoords.y;

                const areaElement = this.dragSession.target;

                // Process the scroll, NOT inverted: the picture follows the mouse
                this.dispatchScrollEvent(deltaX, deltaY, areaElement, 'pointer');

                // Update last position
                this.dragSession.lastCoords = {x: e.clientX, y: e.clientY};

                e.preventDefault();
            }
        };

        this.boundHandlers.pointerUp = async (e: PointerEvent) => {
            if ((e.button === 2 || e.button === 0) && this.dragSession && e.pointerId === pointerId) {
                // Check if this was a right-click without drag
                if (e.button === 2) {
                    const distance = Math.sqrt(
                        Math.pow(e.clientX - this.dragSession.startCoords.x, 2) +
                        Math.pow(e.clientY - this.dragSession.startCoords.y, 2)
                    );

                    // If distance < 3px, this is a click, not a drag
                    if (distance < 3) {
                        const areaElement = this.dragSession.target;

                        // Perform DOM hit-test to identify the target item
                        const targetItemId = findTargetItemId(e);

                        // Call handleRightClickWithoutDrag on the area element
                        await areaElement.handleRightClickWithoutDrag(
                            e.clientX,
                            e.clientY,
                            {
                                ctrlKey: e.ctrlKey,
                                shiftKey: e.shiftKey,
                                altKey: e.altKey,
                                metaKey: e.metaKey
                            },
                            targetItemId
                        );
                    }
                }

                this.dragSession = null;
                this.removeDocumentListeners();
            }
        };

        this.boundHandlers.pointerCancel = (e: PointerEvent) => {
            if (this.dragSession && e.pointerId === pointerId) {
                this.dragSession = null;
                this.removeDocumentListeners();
            }
        };

        this.boundHandlers.keyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.dragSession) {
                this.dragSession = null;
                this.removeDocumentListeners();
            }
        };

        // Add listeners to the document
        document.addEventListener('pointermove', this.boundHandlers.pointerMove);
        document.addEventListener('pointerup', this.boundHandlers.pointerUp);
        document.addEventListener('pointercancel', this.boundHandlers.pointerCancel);
        document.addEventListener('keydown', this.boundHandlers.keyDown);
    }

    /**
     * Attach wheel event listeners
     */
    private attachWheelListeners(): void {
        this.host.addEventListener('wheel', (e: WheelEvent) => {
            // Apply sensitivity and invert (wheel down = scroll up)
            const deltaX = -e.deltaX * (this.config.wheelSensitivity || 1);
            const deltaY = -e.deltaY * (this.config.wheelSensitivity || 1);

            const areaElement = this.findAreaElement(e);
            if (!areaElement) return;

            // Process the scroll
            this.dispatchScrollEvent(deltaX, deltaY, areaElement, 'wheel');

            e.preventDefault();
        }, {passive: false});
    }

    /**
     * Public method to reset the scroll position
     */
    public reset(): void {
        this.scrollStatesByAxisId.clear();
    }

    /**
     * Finds and returns the first instance of O2dAreaElement from the event's composed path.
     *
     * @param {UIEvent} e - The UI event from which to extract the composed path.
     * @return {O2dAreaElement | null} The first O2dAreaElement found in the composed path, or null if none is found.
     */
    private findAreaElement(e: UIEvent): O2dAreaElement | null {
        return e.composedPath().find(elem => (elem as HTMLElement).tagName == 'O2D-AREA') as O2dAreaElement | null;
    }
}